import React, { ReactNode, useEffect } from 'react';
import { CaretDownIcon } from '@phosphor-icons/react';
import { twMerge } from 'tailwind-merge';
import { HighlightType, TimeDomain } from '@/types/video-player';

const MIN_HIGHLIGHT_WIDTH_PX = 3;

interface HighlightProps {
    endTime: number;
    startTime: number;
    type: HighlightType;
    videoDuration: number;
    domain: TimeDomain;
    earliestTimestamp?: number;
}

function clampPercent(value: number): number {
    return Math.min(100, Math.max(0, value));
}

const highlightTypeConfig: Record<
    HighlightType,
    {
        className: string;
        icon: ReactNode;
        zIndex?: number;
    }
> = {
    [HighlightType.EVENT]: {
        className: 'bg-brand/75',
        icon: <CaretDownIcon color="var(--brand)" size={20} weight="fill" />,
        zIndex: 10,
    },
    [HighlightType.FOCUS]: {
        className: 'bg-brand-dark/80',
        icon: null,
        zIndex: 20,
    },
};

export const TrackHighlight: React.FC<HighlightProps> = ({
    endTime,
    startTime,
    type,
    videoDuration,
    domain,
    earliestTimestamp = 0,
}) => {
    const offset = domain === TimeDomain.MEDIA ? 0 : earliestTimestamp;
    const rawLeftPercent = ((startTime - offset) / videoDuration) * 100;
    const rawRightPercent = ((endTime - offset) / videoDuration) * 100;

    const leftPercent = clampPercent(rawLeftPercent);
    const widthPercent = clampPercent(rawRightPercent) - leftPercent;

    const outsideTimeline =
        videoDuration > 0 && (rawRightPercent < 0 || rawLeftPercent > 100);

    useEffect(() => {
        if (!outsideTimeline) return;
        console.warn(
            'TrackHighlight: highlight falls entirely outside the displayed timeline',
            { type, startTime, endTime, earliestTimestamp, videoDuration },
        );
    }, [
        outsideTimeline,
        type,
        startTime,
        endTime,
        earliestTimestamp,
        videoDuration,
    ]);

    if (videoDuration <= 0 || widthPercent <= 0) {
        return null;
    }

    const { className, icon, zIndex } = highlightTypeConfig[type];

    return (
        <div className="absolute inset-0" style={{ zIndex }}>
            <div
                className={twMerge(
                    'absolute top-1/2 -translate-y-1/2 h-[2px] rounded-full',
                    className,
                )}
                style={{
                    left: `${leftPercent}%`,
                    width: `${widthPercent}%`,
                    minWidth: `${MIN_HIGHLIGHT_WIDTH_PX}px`,
                }}
            >
                {icon && (
                    <div
                        className={
                            'absolute bottom-1 left-1/2 -translate-x-1/2 opacity-65'
                        }
                    >
                        {icon}
                    </div>
                )}
            </div>
        </div>
    );
};
